export default class extends HTMLElement {
  static CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'twclid']
  static UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
  static TRACKING_PARAMS = [...this.CLICK_ID_PARAMS, ...this.UTM_PARAMS]
  static OWN_DOMAINS = ['gosign.work', 'app.gosign.work']
  static STORAGE_KEY = 'attr_click_ids'
  static LAST_STORAGE_KEY = 'attr_last_touch'
  static SENT_KEY = 'attr_sent'
  static LAST_SIG_KEY = 'attr_last_sig'
  // Set by the marketing site (sign-unlimited-now src/lib/utm.ts) with
  // domain=.gosign.work, so they are readable here across the subdomain hop.
  static FIRST_COOKIE = '_gosign_attr_first'
  static LAST_COOKIE = '_gosign_attr_last'

  connectedCallback () {
    this.captureParams()
    this.maybeSendAttribution()
  }

  captureParams () {
    this.captureFirstTouch()
    this.captureLastTouch()
  }

  // First-touch: existing keys are never overwritten. Marketing-site cookie
  // data (older — captured on the original landing) wins over URL params
  // (current visit) for keys both provide.
  captureFirstTouch () {
    const stored = this.readJson(localStorage.getItem(this.constructor.STORAGE_KEY)) || {}
    const isFirstVisit = Object.keys(stored).length === 0
    const cookieFirst = this.readCookieJson(this.constructor.FIRST_COOKIE) || {}
    const urlTouch = this.collectUrlTouch()
    let updated = false

    for (const param of this.constructor.TRACKING_PARAMS) {
      const value = cookieFirst[param] || urlTouch[param]
      if (value && !stored[param]) {
        stored[param] = value
        updated = true
      }
    }

    // referrer is first-touch data: backfill whenever it's missing (the
    // marketing cookie may arrive after a direct app visit seeded storage)
    const referrer = cookieFirst.referrer || (isFirstVisit ? urlTouch.referrer : null)
    if (referrer && !stored.referrer) {
      stored.referrer = referrer
      updated = true
    }

    if (isFirstVisit) {
      stored.landing_page = cookieFirst.landing_page || urlTouch.landing_page
      updated = true
    }

    if (updated) {
      localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(stored))
    }
  }

  // Last-touch: most recent wins. Candidates are the current URL (if it
  // carries tracking params), the marketing site's last-touch cookie, and
  // whatever was stored previously — compared by captured_at.
  captureLastTouch () {
    const stored = this.readJson(localStorage.getItem(this.constructor.LAST_STORAGE_KEY))
    const cookieLast = this.readCookieJson(this.constructor.LAST_COOKIE)
    const urlTouch = this.urlHasTrackingParams() ? this.collectUrlTouch() : null

    const winner = [stored, cookieLast, urlTouch].filter(Boolean).reduce((latest, touch) => {
      if (!latest) return touch
      return this.touchTime(touch) >= this.touchTime(latest) ? touch : latest
    }, null)

    if (winner && winner !== stored) {
      localStorage.setItem(this.constructor.LAST_STORAGE_KEY, JSON.stringify(winner))
    }
  }

  touchTime (touch) {
    const t = Date.parse(touch.captured_at || '')
    return Number.isNaN(t) ? 0 : t
  }

  urlHasTrackingParams () {
    const params = new URL(window.location.href).searchParams
    return this.constructor.TRACKING_PARAMS.some((p) => params.get(p))
  }

  collectUrlTouch () {
    const url = new URL(window.location.href)
    const touch = {}

    for (const param of this.constructor.TRACKING_PARAMS) {
      const value = url.searchParams.get(param)
      if (value) touch[param] = value
    }

    const refFromParam = url.searchParams.get('referrer')
    let refFromBrowser = null
    if (!refFromParam && document.referrer) {
      try {
        const host = new URL(document.referrer).hostname
        if (!this.constructor.OWN_DOMAINS.includes(host)) {
          refFromBrowser = host
        }
      } catch { /* invalid referrer URL */ }
    }
    const referrer = refFromParam || refFromBrowser
    if (referrer) touch.referrer = referrer

    touch.landing_page = url.searchParams.get('landing_page') ||
      window.location.pathname
    touch.captured_at = new Date().toISOString()

    return touch
  }

  readJson (raw) {
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      return parsed && Object.keys(parsed).length > 0 ? parsed : null
    } catch {
      return null
    }
  }

  readCookieJson (name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    if (!match) return null
    try {
      return this.readJson(decodeURIComponent(match[1]))
    } catch {
      return null
    }
  }

  maybeSendAttribution () {
    if (this.dataset.signedIn !== 'true') return

    const first = this.readJson(localStorage.getItem(this.constructor.STORAGE_KEY))
    const last = this.readJson(localStorage.getItem(this.constructor.LAST_STORAGE_KEY))

    const firstUnsent = first && !localStorage.getItem(this.constructor.SENT_KEY)
    const lastSig = last ? JSON.stringify(last) : null
    const lastChanged = lastSig && lastSig !== localStorage.getItem(this.constructor.LAST_SIG_KEY)
    if (!firstUnsent && !lastChanged) return

    const payload = { first: first || {}, last: last || {} }

    // Capture GA4 client_id from the _ga cookie
    const gaCookie = document.cookie.split('; ').find(c => c.startsWith('_ga='))
    if (gaCookie) {
      const parts = gaCookie.split('.')
      if (parts.length >= 4) {
        payload.ga_client_id = parts.slice(2).join('.')
      }
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch('/attribution_ids', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(payload)
    }).then(() => {
      localStorage.setItem(this.constructor.SENT_KEY, 'true')
      if (lastSig) localStorage.setItem(this.constructor.LAST_SIG_KEY, lastSig)
      this.enrichClientSdks(payload.first, payload.last)
    }).catch(() => {
      // Silent fail — will retry on next page load
    })
  }

  enrichClientSdks (first, last) {
    // Mirror the server-side trait convention: last-touch unprefixed
    // (canonical), first-touch prefixed with first_.
    const props = {}
    const canonical = Object.keys(last).length > 0 ? last : first
    for (const param of this.constructor.UTM_PARAMS) {
      if (canonical[param]) props[param] = canonical[param]
    }
    if (canonical.referrer) props.referrer = canonical.referrer
    if (canonical.landing_page) props.landing_page = canonical.landing_page

    for (const param of this.constructor.UTM_PARAMS) {
      if (first[param]) props[`first_${param}`] = first[param]
    }
    if (first.referrer) props.first_referrer = first.referrer
    if (first.landing_page) props.first_landing_page = first.landing_page

    if (Object.keys(props).length === 0) return

    if (window.posthog) {
      window.posthog.setPersonProperties(props)
    }

    if (window.cioanalytics) {
      window.cioanalytics.identify({ traits: props })
    }
  }
}
